// --- Dependencies -------------------------------------------------------------------------------
import type { ChartOptions, ScriptableContext, TooltipItem } from "chart.js";
// --- Types --------------------------------------------------------------------------------------
export type TyChartKind = "area" | "bar" | "stackedBar" | "hbar" | "doughnut";

/**
 * The three chart.js controllers the five kinds above map onto. Options are built as the union rather
 * than per kind, because one options object has to cover all three and chart.js types each separately;
 * the component casts it back down at the one point it knows which controller it is rendering.
 */
export type TyChartJsKind = "bar" | "line" | "doughnut";

/**
 * Slots, not colours. A canvas cannot resolve `var(--chart-gold)` — it needs a literal — so a card
 * names the slot it wants and the value is read off `styles.css` here. That keeps the token block the
 * single source of the palette while still letting a chart say what a series *means*.
 *
 * They are named after the frame's own hues rather than after states, because on this dashboard the
 * hue carries the meaning: gold is attention, green is settled, red is late, navy is done.
 */
export type TyChartColor = "gold" | "navy" | "green" | "slate" | "goldLight" | "red" | "muted" | "faint";

export type TyChartTokens = {
    ink: string;
    muted: string;
    grid: string;
    surface: string;
    byName: Record<TyChartColor, string>;
};

// --- Constants ----------------------------------------------------------------------------------

/** Every mark in the app is drawn in Alexandria, so the canvas has to be told — it inherits nothing. */
const FONT = "Alexandria, sans-serif";

/** Mirrors `--font-xs`. The canvas cannot read a CSS token, so the one number lives here. */
const FONT_SIZE = 11;

/** 4px on the data end only, so a bar still grows out of a flat baseline. */
const BAR_RADIUS = { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };

const HBAR_RADIUS = { topLeft: 0, topRight: 4, bottomLeft: 0, bottomRight: 4 };

/** Read only when a token is missing — a stylesheet that failed to load should still draw a chart. */
const FALLBACKS: Record<TyChartColor, string> = {
    gold: "#c7880b",
    navy: "#242e42",
    green: "#3fac89",
    slate: "#627084",
    goldLight: "#e2a430",
    red: "#cf4c4c",
    muted: "#a4a4a4",
    faint: "#d1d5dc",
};

// --- Logic --------------------------------------------------------------------------------------

export function readChartTokens(): TyChartTokens {
    const styles = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;

    return {
        ink: read("--color-dark", "#161b28"),
        muted: read("--color-gray-03", "#6b6b6b"),
        grid: read("--color-light", "#f5f5f5"),
        surface: read("--color-white", "#ffffff"),
        byName: {
            gold: read("--chart-gold", FALLBACKS.gold),
            navy: read("--chart-navy", FALLBACKS.navy),
            green: read("--chart-green", FALLBACKS.green),
            slate: read("--chart-slate", FALLBACKS.slate),
            goldLight: read("--chart-gold-light", FALLBACKS.goldLight),
            red: read("--chart-red", FALLBACKS.red),
            muted: read("--color-gray-02", FALLBACKS.muted),
            faint: read("--color-gray-01", FALLBACKS.faint),
        },
    };
}

/** `#rrggbb` plus an alpha byte. Chart fills need a transparency the tokens themselves must not carry. */
export function withAlpha(hex: string, alpha: number): string {
    const byte = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
        .toString(16)
        .padStart(2, "0");
    return `${hex}${byte}`;
}

/**
 * A vertical fade under an area line. A scriptable option rather than a fixed colour because the
 * gradient needs the canvas height, which does not exist until the chart has been laid out — on the
 * first render `chartArea` is still undefined, hence the guard.
 */
function areaFill(color: string) {
    return (context: ScriptableContext<"line">) => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return withAlpha(color, 0.12);
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, withAlpha(color, 0.28));
        gradient.addColorStop(1, withAlpha(color, 0));
        return gradient;
    };
}

/**
 * One options object for every chart on the dashboard, so grid weight, tick size and tooltip styling
 * cannot drift between two cards sitting side by side. Anything one chart needs differently is a
 * parameter here rather than an override at the call site.
 */
export function buildOptions(
    kind: TyChartKind,
    tokens: TyChartTokens,
    isRtl: boolean,
    formatValue: (value: number) => string,
    hasLegend: boolean
): ChartOptions<TyChartJsKind> {
    const tooltip = {
        backgroundColor: tokens.ink,
        titleColor: tokens.surface,
        bodyColor: tokens.surface,
        titleFont: { family: FONT, size: FONT_SIZE, weight: 600 as const },
        bodyFont: { family: FONT, size: FONT_SIZE },
        padding: 10,
        cornerRadius: 8,
        displayColors: kind !== "doughnut",
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        usePointStyle: true,
        rtl: isRtl,
        textDirection: isRtl ? ("rtl" as const) : ("ltr" as const),
        callbacks: {
            label: (item: TooltipItem<TyChartJsKind>) => {
                const label = item.dataset?.label ?? item.label ?? "";
                /** A bar reads its value off `y`; a doughnut slice has no axis, so `parsed` *is* the value. */
                const parsed = item.parsed as number | { y: number };
                const value = formatValue(Number(typeof parsed === "object" ? parsed.y : parsed));
                return label ? ` ${label}: ${value}` : ` ${value}`;
            },
        },
    };

    const legend = {
        display: hasLegend,
        position: "bottom" as const,
        /** Centred under a centred plot. Left-aligned, it pulled the whole card's weight to one side. */
        align: "center" as const,
        rtl: isRtl,
        textDirection: isRtl ? ("rtl" as const) : ("ltr" as const),
        labels: {
            /** Text wears ink, never the series colour — the swatch beside it carries the identity. */
            color: tokens.muted,
            font: { family: FONT, size: FONT_SIZE },
            usePointStyle: true,
            pointStyle: "circle" as const,
            boxWidth: 8,
            boxHeight: 8,
            padding: 14,
        },
    };

    const ticks = { color: tokens.muted, font: { family: FONT, size: FONT_SIZE }, padding: 4 };

    const base: ChartOptions<TyChartJsKind> = {
        responsive: true,
        maintainAspectRatio: false,
        /** Recessive by design: the marks carry the story, the frame only has to stay out of the way. */
        interaction: { mode: "index" as const, intersect: false },
        animation: { duration: 320 },
        plugins: { legend, tooltip },
    };

    if (kind === "doughnut") {
        return {
            ...base,
            /*
             * A thick ring, not a hairline one. At 66% the arcs were thin enough that two adjacent
             * slices of similar size read as one band; the ring is the mark here, so it gets the room.
             */
            cutout: "54%",
            interaction: { mode: "nearest" as const, intersect: true },
            plugins: { ...base.plugins, legend: { ...legend, display: true } },
        };
    }

    const valueAxis = {
        beginAtZero: true,
        border: { display: false },
        grid: { color: tokens.grid, drawTicks: false },
        /**
         * `precision: 0` because every count on this dashboard is a whole thing — a unit, a lead, a
         * contract. Left to itself chart.js divides a max of 1 into 0 / 0.5 / 1, and half a unit is
         * not a quantity that exists.
         */
        ticks: { ...ticks, maxTicksLimit: 5, precision: 0, callback: (value: number | string) => formatValue(Number(value)) },
    };

    const categoryAxis = {
        border: { display: false },
        grid: { display: false },
        ticks: { ...ticks, maxRotation: 0, autoSkipPadding: 12 },
    };

    if (kind === "hbar") {
        return {
            ...base,
            indexAxis: "y" as const,
            interaction: { mode: "nearest" as const, intersect: true },
            scales: { x: valueAxis, y: categoryAxis },
        };
    }

    return {
        ...base,
        scales: { x: categoryAxis, y: valueAxis },
        ...(kind === "stackedBar" ? { scales: { x: { ...categoryAxis, stacked: true }, y: { ...valueAxis, stacked: true } } } : {}),
    };
}

/**
 * Turns one logical series into a chart.js dataset. Kept beside the options so a mark's geometry —
 * line weight, point size, bar thickness, the gap between stacked segments — is decided in one file.
 */
export function buildDataset(kind: TyChartKind, label: string, data: number[], color: string, tokens: TyChartTokens, colors?: string[]) {
    /** 3px of the card's own colour between slices, so touching arcs never bleed into one another. */
    if (kind === "doughnut") return { label, data, backgroundColor: colors ?? [color], borderColor: tokens.surface, borderWidth: 3, hoverOffset: 8 };

    if (kind === "area") {
        return {
            label,
            data,
            borderColor: color,
            backgroundColor: areaFill(color),
            /** 3px: a 2px line against a tinted fill is the first thing to disappear on a phone. */
            borderWidth: 3,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            /** Invisible until hovered, then big enough to aim at — 5px is the smallest honest target. */
            pointHoverRadius: 5,
            pointHoverBorderWidth: 2,
            pointHoverBorderColor: tokens.surface,
            pointHoverBackgroundColor: color,
            pointBackgroundColor: color,
        };
    }

    return {
        label,
        data,
        backgroundColor: colors ?? color,
        borderRadius: kind === "hbar" ? HBAR_RADIUS : BAR_RADIUS,
        borderSkipped: false,
        /** The 2px surface gap that keeps stacked segments from reading as one solid block. */
        borderColor: tokens.surface,
        borderWidth: kind === "stackedBar" ? { top: 2, right: 0, bottom: 0, left: 0 } : 0,
        maxBarThickness: kind === "hbar" ? 20 : 32,
    };
}
